import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, Col, Row, Table } from 'react-bootstrap';
import airbnbApi from './api';
import { ErrorAlert, FlowASteps, PageLoader, StatusBadge, formatTime } from './common';

const Info = ({ label, children }) => (
  <Col md={3} className="margbot10">
    <div className="fnt12 text-muted">{label}</div>
    <div>{children || '—'}</div>
  </Col>
);

const PropertyManagerDetail = () => {
  const { id } = useParams();
  const [pm, setPm] = useState(null);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    airbnbApi.getPropertyManager(id).then(setPm).catch(setError);
    airbnbApi.listProperties(id).then(setProperties).catch(setError);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Re-running a PM step can change the PM (nextpax_code) and its properties
  const onStepChange = () => {
    load();
    setRefreshKey(k => k + 1);
  };

  if (!pm) return <div className="pad20"><ErrorAlert error={error} />{!error && <PageLoader />}</div>;

  const mainContact = (pm.contacts || []).find(c => c.role === 'main');

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13"><Link to="/airbnb">Property managers</Link> / {pm.company_name}</div>
      <div className="d-flex justify-content-between align-items-center margbot15">
        <h4 className="mb-0">{pm.company_name}</h4>
        <div>
          {pm.nextpax_code && (
            <Link to={`/airbnb/property-managers/${id}/nextpax-properties`} className="btn btn-outline-secondary btn-sm me-2">
              NextPax properties
            </Link>
          )}
          <Link to={`/airbnb/property-managers/${id}/edit`} className="btn btn-outline-primary btn-sm">Edit</Link>
          <Link to={`/airbnb`} className="btn btn-outline-primary btn-sm ms-2">Back</Link>

        </div>
      </div>
      <ErrorAlert error={error} onClose={() => setError(null)} />

      <Card className="margbot15 mt-2">
        <Card.Body>
          <Row>
            <Info label="NextPax code">{pm.nextpax_code || <span className="text-muted">not created yet</span>}</Info>
            <Info label="Email">{pm.company_email}</Info>
            <Info label="Phone">{pm.company_phone}</Info>
            <Info label="Address">{[pm.address, pm.city, pm.postal_code, pm.country_code].filter(Boolean).join(', ')}</Info>
            <Info label="Main currency">{pm.main_currency}</Info>
            <Info label="Accepted currencies">{(pm.accepted_currencies || []).join(', ')}</Info>
            <Info label="Payment methods">{(pm.accepted_payment_methods || []).join(', ')}</Info>
            <Info label="Languages">{(pm.spoken_languages || []).join(', ')}</Info>
            <Info label="Check-in">{pm.check_in_from} – {pm.check_in_until}</Info>
            <Info label="Check-out">{pm.check_out_from} – {pm.check_out_until}</Info>
            <Info label="Main contact">
              {mainContact && `${mainContact.firstName} ${mainContact.lastName} · ${mainContact.email}`}
            </Info>
            <Info label="Contacts">{(pm.contacts || []).length}</Info>
            <Info label="Airbnb host ID">{pm.airbnb_host_id}</Info>
            <Info label="Airbnb policy">{pm.airbnb_channel_policy_id}</Info>
            <Info label="Airbnb settings">{Object.keys(pm.airbnb_settings || {}).length} set</Info>
            <Info label="Host account connected">{pm.host_account_connected_at && formatTime(pm.host_account_connected_at)}</Info>
          </Row>
        </Card.Body>
      </Card>

      <Card className="margbot15 mt-2">
        <Card.Header className="bld">Flow A — property manager steps</Card.Header>
        <Card.Body>
          <p className="fnt12 text-muted">
            Cancellation policies sent in step 2 are collected from this property manager's properties.
            Step 5 is done by hand in the NextPax Core UI and on airbnb.com/host/homes; running it only records that.
          </p>
          <FlowASteps subjectPath={`property_managers/${id}`} bulkPmId={id} onChange={onStepChange} refreshKey={refreshKey} />
        </Card.Body>
      </Card>

      <Card className="margbot15 mt-2">
        <Card.Header className="bld d-flex justify-content-between align-items-center">
          Properties ({properties.length})
          <Link to={`/airbnb/property-managers/${id}/properties/new`} className="btn btn-primary btn-sm">Add property</Link>
        </Card.Header>
        <Card.Body>
          {properties.length === 0 ? (
            <div className="text-muted">No properties yet.</div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover size="sm" className="bg1 mb-0">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>Hotel code</th>
                    <th>Type</th>
                    <th>Onboarding</th>
                    <th>Room types</th>
                    <th>NextPax property ID</th>
                    <th>Airbnb listing ID</th>
                    <th>Images</th>
                    <th>Airbnb build</th>
                    <th>Channel status</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map(p => (
                    <tr key={p.id}>
                      <td><Link to={`/airbnb/properties/${p.id}`}>{p.hotel_name || `GH ${p.gh_detail_id}`}</Link></td>
                      <td>{p.hotel_code}</td>
                      <td>{p.type_code} · {p.classification}</td>
                      <td>{p.onboarding_mode === 'new' ? 'New listing' : 'Takeover'}</td>
                      <td>{p.classification === 'multi-unit' ? p.unit_types_count : '—'}</td>
                      <td>{p.nextpax_property_id || <span className="text-muted">not created</span>}</td>
                      <td>{p.airbnb_listing_id || (p.listing_id_required ? <span className="text-danger">required</span> : 'not needed')}</td>
                      <td className={p.images_count < 5 ? 'text-danger' : ''}>{p.images_count}</td>
                      <td><StatusBadge status={p.airbnb_build_status} /></td>
                      <td className="fnt12">{p.airbnb_channel_property_status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PropertyManagerDetail;
